// Derive the tenant's REAL call flow from /api/settings/voice-routing/.
//
// Everything here is computed from live data. There is no sample flow and no
// placeholder node: if a tenant has no menu, the screen says so, because a
// drawn menu that does not exist is the same lie as a chart of zeros.
//
// The shapes this reads are the ones the CRM actually resolves a call with —
// CallRoutingPolicy decides answering, TenantVoiceProfile carries the menu,
// greeting and voicemail, RingGroup/RingGroupMember carry the order. Where the
// two disagree the POLICY wins, exactly as internal_voice does it, so the
// picture matches the phone rather than matching the settings form.

export const NODE = {
  HOURS: 'hours',
  MENU: 'ivr',
  RING: 'ring',
  AI: 'ai',
  VOICEMAIL: 'voicemail',
};

/** Answer mode, resolved the way the backend resolves it when unset. */
export function answerMode(policy = {}) {
  const explicit = String(policy.answer_mode || '').trim();
  if (explicit) return explicit;
  if (policy.ai_override_enabled || policy.ai_first) return 'ai_first';
  return String(policy.after_hours_route || '').toUpperCase() === 'AI'
    ? 'ai_first'
    : 'ring_then_voicemail';
}

/** Is the tenant inside posted hours RIGHT NOW, by the tenant's own timezone. */
export function openNow(policy = {}, now = new Date()) {
  const schedule = policy.business_hours_schedule;
  const tz = policy.timezone || 'UTC';
  let hhmm;
  let weekday;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hour12: false, hour: '2-digit', minute: '2-digit', weekday: 'short',
    }).formatToParts(now);
    const get = (t) => (parts.find((p) => p.type === t) || {}).value || '';
    hhmm = `${get('hour')}:${get('minute')}`;
    weekday = get('weekday').toLowerCase().slice(0, 3);
  } catch {
    // An invalid timezone string (Hodges stores the literal "EST.") throws here.
    // The backend catches the same failure and falls back to the server default,
    // so the honest answer is "we cannot tell", not a confident open/closed.
    return { open: true, known: false, reason: `timezone "${tz}" is not a real timezone` };
  }

  if (schedule && schedule.enabled) {
    const day = schedule[weekday] || schedule[`${weekday}day`];
    if (!day || day.closed) return { open: false, known: true };
    if (day.start && day.end) return { open: hhmm >= day.start && hhmm <= day.end, known: true };
    return { open: true, known: true };
  }

  const start = (policy.business_hours_start || '').slice(0, 5);
  const end = (policy.business_hours_end || '').slice(0, 5);
  if (!start || !end) {
    // The backend returns TRUE when either bound is missing, so the line is
    // effectively always open. Sun Pest runs exactly this way — a start of
    // 08:00 and no end — and nobody reading the settings form would know.
    return { open: true, known: true, alwaysOpen: true };
  }
  if (start === end) return { open: true, known: true };
  if (start < end) return { open: hhmm >= start && hhmm <= end, known: true };
  return { open: hhmm >= start || hhmm <= end, known: true };
}

/** Members of a group, in dial order, each marked with whether it can ring. */
export function groupMembers(group = {}, usersById = {}) {
  return [...(group.members || [])]
    .filter((m) => m.is_active !== false)
    .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
    .map((m) => {
      const user = usersById[m.user_id] || {};
      const fallback = (m.fallback_number || '').trim();
      // ABSENCE OF EVIDENCE IS NOT EVIDENCE OF REACHABILITY.
      //
      // `has_app` is a newer field. Against an API that predates it the honest
      // answer is "unknown", NOT "reachable" — defaulting a missing field to
      // false would invent a warning, and defaulting it to true would hide the
      // exact failure this screen exists to surface (a member with no app and
      // no backup is dropped at dial time and rings nothing). So unknown stays
      // unknown and the validation strip says it cannot tell yet.
      const appKnown = typeof user.has_app === 'boolean';
      const hasApp = appKnown ? user.has_app : null;
      // Matches internal_voice._ordered_ring_sequence_from_policy: an app leg
      // OR a PSTN backup makes a member dialable.
      const canRing = fallback ? true : (appKnown ? user.has_app : null);
      return {
        userId: m.user_id,
        name: user.name || `User ${m.user_id}`,
        extension: user.extension || '',
        hasApp,
        appKnown,
        fallback,
        timeout: m.ring_timeout || null,
        canRing,
      };
    });
}

/**
 * Build the node graph. Returns { nodes, edges, entry } where every node names
 * where a caller goes next — an unnamed exit is a dead end and is reported.
 */
export function buildFlow(data, now = new Date()) {
  const policy = data.policy || {};
  const profile = data.profile || {};
  const usersById = Object.fromEntries((data.available_users || []).map((u) => [u.id, u]));
  const groups = [...(data.ring_groups || [])]
    .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
    // `id` is newer on the wire. Falling back to position keeps the edges
    // drawable against an older server instead of pointing at "ring:undefined",
    // which rendered as a dead end that isn't one.
    .map((g, i) => ({ ...g, id: g.id ?? `idx${i}` }));
  const mode = answerMode(policy);
  const hours = openNow(policy, now);
  // THE POLICY WINS, because that is what the resolver does. The inbound
  // resolve forces route_mode="ai_terminal" whenever the active
  // CallRoutingPolicy says ai_first or ai_override_enabled, no matter what
  // TenantVoiceProfile.ai.enabled says. t7 is live proof: its policy says
  // ai_first while its profile says the AI is off. Reading the profile alone
  // would tell t7's owner that calls ring a person, when in fact the assistant
  // answers every one — the screen would be confidently wrong about the single
  // most important thing on it.
  const aiEnabled = Boolean(
    policy.ai_first || policy.ai_override_enabled || (profile.ai || {}).enabled,
  );
  const aiByPolicyOnly = Boolean(
    (policy.ai_first || policy.ai_override_enabled) && !(profile.ai || {}).enabled,
  );
  const menu = profile.ivr && Object.keys(profile.ivr).length ? profile.ivr : null;
  const afterHours = String(policy.after_hours_route || 'VOICEMAIL').toUpperCase();

  const nodes = [];
  const push = (n) => { nodes.push(n); return n.id; };

  push({
    id: 'hours',
    type: NODE.HOURS,
    title: 'Business hours',
    detail: policy.business_hours_schedule && policy.business_hours_schedule.enabled
      ? 'Per-day schedule'
      : [policy.business_hours_start, policy.business_hours_end].filter(Boolean).join(' – ') || 'Not set',
    timezone: policy.timezone || 'UTC',
    hours,
    next: { open: mode === 'ai_first' && aiEnabled ? 'ai' : (menu ? 'menu' : (groups[0] ? `ring:${groups[0].id}` : 'voicemail')),
            closed: afterHours === 'AI' ? 'ai' : (afterHours === 'RING_GROUP' && groups[0] ? `ring:${groups[0].id}` : 'voicemail') },
  });

  if (menu) {
    push({
      id: 'menu', type: NODE.MENU, title: 'Main menu',
      options: menu,
      detail: `${Object.keys(menu).length} keys`,
      next: {},
    });
  }

  groups.forEach((g) => {
    const members = groupMembers(g, usersById);
    push({
      id: `ring:${g.id}`, type: NODE.RING, title: g.name || 'Ring group',
      members,
      detail: `${members.length} member${members.length === 1 ? '' : 's'}`,
      timeout: policy.default_ring_timeout || 15,
      // Ring groups hand back to the AI when it is on, otherwise voicemail.
      next: { noAnswer: aiEnabled ? 'ai' : 'voicemail' },
    });
  });

  if (aiEnabled) {
    push({
      id: 'ai', type: NODE.AI, title: 'AI assistant',
      greeting: (profile.ai || {}).greeting || policy.greeting_open || '',
      placement: aiPlacement({ mode, afterHours, groups, hours }),
      detail: aiPlacementLabel({ mode, afterHours, groups }),
      next: { escalate: groups[0] ? `ring:${groups[0].id}` : 'voicemail' },
    });
  }

  push({
    id: 'voicemail', type: NODE.VOICEMAIL, title: 'Voicemail',
    detail: (profile.voicemail || {}).transcription ? 'Transcribed' : 'Not transcribed',
    greeting: (profile.voicemail || {}).greeting || policy.greeting_voicemail || '',
    next: {},
  });

  return { nodes, mode, hours, aiEnabled, aiByPolicyOnly, menu, groups, usersById, policy, profile };
}

function aiPlacement({ mode, afterHours, groups }) {
  if (mode === 'ai_first') return 'answers_first';
  if (afterHours === 'AI') return 'after_hours';
  if (groups.length) return 'after_ring';
  return 'instead_of_voicemail';
}

function aiPlacementLabel({ mode, afterHours, groups }) {
  const p = aiPlacement({ mode, afterHours, groups });
  return {
    answers_first: 'Answers first, before anyone rings',
    after_hours: 'After hours only',
    after_ring: 'After the ring group goes unanswered',
    instead_of_voicemail: 'Instead of voicemail',
  }[p];
}

/**
 * The validation strip, computed from the live flow. Each check returns
 * {id, level: 'pass'|'warn'|'fail', text}. Nothing here is decorative — every
 * check reads a real field and names the real consequence.
 */
export function validateFlow(flow) {
  const out = [];
  const { nodes, menu, groups, aiEnabled, policy, profile, hours } = flow;

  // 1. Dead ends — a branch that ends nowhere a caller can be heard.
  const terminals = nodes.filter((n) => n.type === NODE.VOICEMAIL || n.type === NODE.AI);
  const ringWithNoExit = nodes.filter(
    (n) => n.type === NODE.RING && !(n.next && n.next.noAnswer),
  );
  if (!terminals.length) {
    out.push({ id: 'deadend', level: 'fail',
      text: 'No voicemail and no AI. A caller nobody picks up for is hung up on.' });
  } else if (ringWithNoExit.length) {
    out.push({ id: 'deadend', level: 'fail',
      text: `${ringWithNoExit.length} ring group${ringWithNoExit.length === 1 ? '' : 's'} with nowhere to go when nobody answers.` });
  } else {
    out.push({ id: 'deadend', level: 'pass',
      text: 'Every branch ends somewhere a caller can be heard.' });
  }

  // 2. Menu keys — every advertised key routed.
  if (!menu) {
    out.push({ id: 'keys', level: 'pass',
      text: 'No phone menu configured, so there are no keys to route.' });
  } else {
    const unrouted = Object.entries(menu).filter(([, dest]) => !dest);
    out.push(unrouted.length
      ? { id: 'keys', level: 'fail', text: `Keys ${unrouted.map(([k]) => k).join(', ')} play in the menu but route nowhere.` }
      : { id: 'keys', level: 'pass', text: `Keys ${Object.keys(menu).join(', ')} are each routed.` });
  }

  // 3. Members who cannot ring — the Sun Pest failure.
  const ringNodes = nodes.filter((n) => n.type === NODE.RING);
  const dead = [];
  let unknown = 0;
  ringNodes.forEach((n) => {
    (n.members || []).forEach((m) => {
      if (m.canRing === false) dead.push({ group: n.title, ...m });
      else if (m.canRing === null) unknown += 1;
    });
  });
  const allDeadGroups = ringNodes.filter(
    (n) => n.members.length && n.members.every((m) => m.canRing === false),
  );
  if (!groups.length) {
    out.push({ id: 'ring', level: 'pass', text: 'No ring groups, so nobody is dialled.' });
  } else if (allDeadGroups.length) {
    out.push({ id: 'ring', level: 'fail',
      text: `${allDeadGroups.map((g) => g.title).join(', ')}: nobody in the group can ring. Every transfer there is silence, then the fallback.` });
  } else if (dead.length) {
    out.push({ id: 'ring', level: 'warn',
      text: `${dead.map((d) => d.name).join(', ')} ${dead.length === 1 ? 'has' : 'have'} no app and no backup number, so ${dead.length === 1 ? 'that place in the order does nothing' : 'those places in the order do nothing'} — they are skipped at dial time.` });
  } else if (unknown) {
    // Honest "cannot tell" rather than a green tick this data can't support.
    out.push({ id: 'ring', level: 'warn',
      text: `Can’t tell whether ${unknown === 1 ? 'one member' : `${unknown} members`} can actually ring — this server does not report registered devices yet. A member with no app and no backup number is skipped at dial time.` });
  } else {
    out.push({ id: 'ring', level: 'pass', text: 'Everyone in every ring group can actually ring.' });
  }

  // 4a. The two config stores disagreeing about whether the AI answers.
  if (flow.aiByPolicyOnly) {
    out.push({ id: 'aisplit', level: 'warn',
      text: 'The answering rules say the assistant answers, and the voice profile says it is switched off. The rules win, so it does answer — but two settings screens will show you opposite things until one is corrected.' });
  }

  // 4. The AI must not offer what it has no tool for.
  if (aiEnabled) {
    const grants = (profile.ai || {}).grants;
    if (!grants) {
      out.push({ id: 'tools', level: 'warn',
        text: 'What the assistant is allowed to do is not configured yet, so it is bounded only by its prompt. Tool grants arrive with editing.' });
    } else {
      const missing = Object.entries(grants)
        .filter(([, on]) => on)
        .map(([k]) => k)
        .filter((k) => !(flow.availableTools || []).includes(k));
      out.push(missing.length
        ? { id: 'tools', level: 'fail', text: `The assistant is allowed to ${missing.join(', ')}, and there is no tool behind ${missing.length === 1 ? 'it' : 'them'}. It would promise and not deliver.` }
        : { id: 'tools', level: 'pass', text: 'Everything the assistant may offer has a real tool behind it.' });
    }
  }

  // 5. Hours sanity — the quiet one that bit Hodges and Sun Pest.
  if (hours.known === false) {
    out.push({ id: 'hours', level: 'fail', text: `${hours.reason}. Hours are being computed in UTC instead, so open and closed are wrong.` });
  } else if (hours.alwaysOpen) {
    out.push({ id: 'hours', level: 'warn',
      text: 'Only one end of the business day is set, so the line counts as open around the clock and after-hours routing never fires.' });
  } else if (!policy.business_hours_start && !policy.business_hours_end
             && !(policy.business_hours_schedule || {}).enabled) {
    out.push({ id: 'hours', level: 'warn', text: 'No business hours set, so the line is always treated as open.' });
  } else {
    out.push({ id: 'hours', level: 'pass', text: 'Business hours are set on both ends and in a real timezone.' });
  }

  return out;
}
